import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, RefreshCw, ShieldCheck, Search, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function InsuredBK() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [checkForm, setCheckForm] = useState({ name: "", unique_id: "", city: "" });
  const [checkResult, setCheckResult] = useState(null);

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const { data } = await api.get(`/verification/insured-bk/list`, { params: { q: q || undefined, limit: 200 } });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(""); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/verification/insured-bk/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded — ${data.inserted} new, ${data.updated} updated, roster now ${data.total_roster}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  const check = async () => {
    if (!checkForm.name || !checkForm.unique_id) { toast.error("name + unique id required"); return; }
    setBusy("check");
    try {
      const { data } = await api.post(`/verification/insured-bk/check`, checkForm);
      setCheckResult(data);
      toast[data.eligible ? "success" : "error"](data.eligible ? "Eligible" : `Not eligible — ${data.reason}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setBusy(""); }
  };

  const remove = async (key) => {
    if (!window.confirm("Remove this teacher from the roster?")) return;
    try {
      await api.delete(`/verification/insured-bk/${encodeURIComponent(key)}`);
      toast.success("Removed"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck /> Insured BK Teacher Verification</h1>
        <p className="text-sm text-muted-foreground">
          Only teachers whose (name, unique ID, city) appear in this roster can be approved as
          <b> BK Teacher (Insured)</b> — they receive a complimentary Full Body Checkup.
        </p>
      </div>

      <Card><CardContent className="p-4 space-y-2">
        <div className="text-sm font-semibold">1) Upload roster (.xlsx / .csv)</div>
        <div className="text-xs text-slate-500">Expected columns: Name, Unique ID, City, State (optional), Phone (optional), Insurance Provider (optional), Policy No (optional). Headers case-insensitive.</div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="file" accept=".csv,.xlsx,.xlsm" className="hidden" data-testid="ibk-upload-input"
                 onChange={(e) => onUpload(e.target.files?.[0])} />
          <span className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm">
            {busy === "upload" ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload roster
          </span>
        </label>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-2">
        <div className="text-sm font-semibold">2) Quick eligibility check</div>
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="Full name" className="w-56 h-9" value={checkForm.name} onChange={(e) => setCheckForm({ ...checkForm, name: e.target.value })} data-testid="ibk-check-name" />
          <Input placeholder="Unique ID" className="w-40 h-9" value={checkForm.unique_id} onChange={(e) => setCheckForm({ ...checkForm, unique_id: e.target.value })} data-testid="ibk-check-uid" />
          <Input placeholder="City" className="w-40 h-9" value={checkForm.city} onChange={(e) => setCheckForm({ ...checkForm, city: e.target.value })} data-testid="ibk-check-city" />
          <Button size="sm" onClick={check} disabled={busy === "check"} data-testid="ibk-check-btn">
            {busy === "check" ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} Check
          </Button>
        </div>
        {checkResult && (
          <div className={`rounded p-2 text-xs ${checkResult.eligible ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-rose-50 text-rose-900 border border-rose-200"}`} data-testid="ibk-check-result">
            <b>{checkResult.eligible ? "✅ Eligible" : "❌ Not eligible"}</b>
            {checkResult.reason && <span> — {checkResult.reason}</span>}
            {checkResult.warnings?.length > 0 && <div className="mt-1 text-amber-800">⚠ {checkResult.warnings.join("; ")}</div>}
            {checkResult.roster && <div className="mt-1 opacity-70">Roster match: {checkResult.roster.name} · {checkResult.roster.unique_id} · {checkResult.roster.city}</div>}
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-3 flex gap-2 items-end flex-wrap">
        <Search size={14} className="text-slate-500 mb-2" />
        <Input placeholder="Search name / unique id / city" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-72 h-9" data-testid="ibk-search" />
        <Button size="sm" onClick={load} disabled={busy === "load"} data-testid="ibk-refresh"><RefreshCw size={14} /></Button>
        <div className="ml-auto text-xs text-slate-500">Roster size: <b>{total}</b></div>
      </CardContent></Card>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Unique ID</TableHead>
              <TableHead>City</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No teachers in roster. Upload the file above.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.key}>
                <TableCell className="text-sm font-medium">{r.name}</TableCell>
                <TableCell className="font-mono text-xs">{r.unique_id}</TableCell>
                <TableCell className="text-xs">{r.city || "—"}</TableCell>
                <TableCell className="text-xs">{r.state || "—"}</TableCell>
                <TableCell className="text-xs">{r.phone || "—"}</TableCell>
                <TableCell className="text-xs">{r.insurance_provider || "—"}<div className="text-[10px] text-slate-500">{r.policy_no}</div></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => remove(r.key)}><Trash2 size={12} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
